import { type FC } from 'react';
import { type SingleValue } from 'react-select';
import { RiAlertFill } from 'react-icons/ri';
import Select from '@/components/UI/Select';
import { errorToast } from '@/helpers.tsx';
import { _isNotEmpty, _notNil } from '@/littledash';
import {
  type SampleDefinitionFormData,
  type SampleDefinitionIssue,
  type SampleDefinitionIssueAction,
} from '@/model/SamplePlan.model';
import styles from './SampleDefinitionIssuesModal.module.scss';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import InVivoError from '@/model/InVivoError';

// Extract bulk actions and helper actions from the main action type
export type BulkIssueAction =
  | Extract<
      SampleDefinitionIssueAction,
      | { type: 'bulk-remove-definitions' }
      | { type: 'bulk-clear-invalid-metadata' }
      | { type: 'bulk-clear-invalid-metadata-options' }
      | { type: 'bulk-remove-invalid-details' }
      | { type: 'bulk-remove-invalid-targets' }
      | { type: 'close-modal' }
    >
  | { type: 'open-sample-plan-modal' };

interface SampleDefinitionIssueCardProps {
  issue: SampleDefinitionIssue;
  affectedDefinitions: Array<SampleDefinitionFormData>;
  onAction: (action: BulkIssueAction) => void;
}

type IssueActionOption = { value: string; label: string };

export const SampleDefinitionIssueCard: FC<SampleDefinitionIssueCardProps> = ({
  issue,
  affectedDefinitions,
  onAction,
}) => {
  if (affectedDefinitions.length === 0) {
    return null;
  }

  switch (issue.type) {
    case 'archived-sample-type':
      return <ArchivedSampleTypeCard affectedDefinitions={affectedDefinitions} issue={issue} onAction={onAction} />;
    case 'missing-sample-type':
      return <MissingSampleTypeCard affectedDefinitions={affectedDefinitions} issue={issue} onAction={onAction} />;
    case 'invalid-sample-detail-type':
      return <InvalidDetailsCard affectedDefinitions={affectedDefinitions} issue={issue} onAction={onAction} />;
    case 'inactive-metadata':
      return <InactiveMetadataCard affectedDefinitions={affectedDefinitions} issue={issue} onAction={onAction} />;
    case 'missing-metadata':
      return <MissingMetadataCard affectedDefinitions={affectedDefinitions} issue={issue} onAction={onAction} />;
    case 'invalid-metadata-value':
      return <InvalidMetadataValueCard affectedDefinitions={affectedDefinitions} issue={issue} onAction={onAction} />;
    case 'missing-required-metadata':
      return (
        <MissingRequiredMetadataCard affectedDefinitions={affectedDefinitions} issue={issue} onAction={onAction} />
      );
    case 'invalid-target':
      return <InvalidTargetCard affectedDefinitions={affectedDefinitions} issue={issue} onAction={onAction} />;
    default:
      return null;
  }
};

const AffectedDefinitionsList: FC<{ definitions: Array<SampleDefinitionFormData> }> = ({ definitions }) => {
  if (definitions.length === 1) {
    return (
      <div className={styles.affectedDefinitions}>
        <strong>Affected sample definition:</strong> {definitions[0].name}
      </div>
    );
  }

  return (
    <div className={styles.affectedDefinitions}>
      <strong>Affected sample definitions ({definitions.length}):</strong>
      <ul className="ma0 mt2 pl3">
        {definitions.map((def) => (
          <li key={def.api_id}>{def.name}</li>
        ))}
      </ul>
    </div>
  );
};

const ArchivedSampleTypeCard: FC<{
  affectedDefinitions: Array<SampleDefinitionFormData>;
  issue: Extract<SampleDefinitionIssue, { type: 'archived-sample-type' }>;
  onAction: (action: BulkIssueAction) => void;
}> = ({ affectedDefinitions, issue, onAction }) => {
  const actionOptions: Array<IssueActionOption> = [
    { value: 'change-sample-type', label: 'Change sample type' },
    { value: 'remove-definition', label: 'Remove definition' },
  ];

  const definitionIds = affectedDefinitions.map((def) => def.api_id);

  const handleActionSelect = (action: SingleValue<IssueActionOption>) => {
    switch (action?.value) {
      case 'change-sample-type':
        onAction({ type: 'open-sample-plan-modal' });
        break;
      case 'remove-definition':
        onAction({ type: 'bulk-remove-definitions', definition_ids: definitionIds });
        break;
      default:
        errorToast('Something went wrong, please try again');
    }
  };

  return (
    <div className={styles.issueCard} data-testid="archived-sample-type-card">
      <div className="flex">
        <div className="pr2 pl1">
          <RiAlertFill className={`red ${styles.alertIcon}`} size={16} />
        </div>
        <div className={styles.issueContent}>
          <div className={styles.issueTitle}>Sample type archived</div>
          <div className={styles.issueDescription}>
            The sample type "{issue.sample_type_name}" has been archived and can no longer be used.
          </div>
          <AffectedDefinitionsList definitions={affectedDefinitions} />
          <Select
            options={actionOptions}
            placeholder="Select fix to apply"
            onChange={handleActionSelect}
            isMulti={false}
          />
        </div>
      </div>
    </div>
  );
};

const MissingSampleTypeCard: FC<{
  affectedDefinitions: Array<SampleDefinitionFormData>;
  issue: Extract<SampleDefinitionIssue, { type: 'missing-sample-type' }>;
  onAction: (action: BulkIssueAction) => void;
}> = ({ affectedDefinitions, issue, onAction }) => {
  const actionOptions: Array<IssueActionOption> = [
    { value: 'remove-definition', label: 'Remove definition' },
    { value: 'remove-and-edit', label: 'Remove and recreate' },
  ];

  const definitionIds = affectedDefinitions.map((def) => def.api_id);

  const handleActionSelect = (action: SingleValue<IssueActionOption>) => {
    switch (action?.value) {
      case 'remove-definition':
        onAction({ type: 'bulk-remove-definitions', definition_ids: definitionIds });
        break;
      case 'remove-and-edit':
        onAction({ type: 'open-sample-plan-modal' });
        break;
      default:
        errorToast('Something went wrong, please try again');
    }
  };

  return (
    <div className={styles.issueCard} data-testid="missing-sample-type-card">
      <div className="flex">
        <div className="pr2 pl1">
          <RiAlertFill className={`red ${styles.alertIcon}`} size={16} />
        </div>
        <div className={styles.issueContent}>
          <div className={styles.issueTitle}>Sample type not found</div>
          <div className={styles.issueDescription}>
            The sample type (ID: {issue.sample_type_id}) no longer exists in the glossary.
          </div>
          <AffectedDefinitionsList definitions={affectedDefinitions} />
          <Select
            options={actionOptions}
            placeholder="Select fix to apply"
            onChange={handleActionSelect}
            isMulti={false}
          />
        </div>
      </div>
    </div>
  );
};

const InvalidDetailsCard: FC<{
  affectedDefinitions: Array<SampleDefinitionFormData>;
  issue: Extract<SampleDefinitionIssue, { type: 'invalid-sample-detail-type' }>;
  onAction: (action: BulkIssueAction) => void;
}> = ({ affectedDefinitions, issue, onAction }) => {
  const actionOptions: Array<IssueActionOption> = [
    { value: 'remove-invalid-details', label: 'Remove invalid details' },
    { value: 'remove-definition', label: 'Remove entire definition' },
  ];

  const definitionIds = affectedDefinitions.map((def) => def.api_id);

  const handleActionSelect = (action: SingleValue<IssueActionOption>) => {
    switch (action?.value) {
      case 'remove-invalid-details':
        onAction({
          type: 'bulk-remove-invalid-details',
          definition_ids: definitionIds,
          detail_types: issue.invalid_detail_types,
        });
        break;
      case 'remove-definition':
        onAction({ type: 'bulk-remove-definitions', definition_ids: definitionIds });
        break;
      default:
        errorToast('Something went wrong, please try again');
    }
  };

  return (
    <div className={styles.issueCard} data-testid="invalid-details-card">
      <div className="flex">
        <div className="pr2 pl1">
          <RiAlertFill className={`red ${styles.alertIcon}`} size={16} />
        </div>
        <div className={styles.issueContent}>
          <div className={styles.issueTitle}>Invalid sample details</div>
          <div className={styles.issueDescription}>
            {issue.invalid_detail_types.length} detail type{issue.invalid_detail_types.length > 1 ? 's' : ''} (
            {issue.invalid_detail_types.join(', ')}) {issue.invalid_detail_types.length > 1 ? 'are' : 'is'} no longer
            available for sample type "{issue.sample_type_name}".
          </div>
          <AffectedDefinitionsList definitions={affectedDefinitions} />
          <Select
            options={actionOptions}
            placeholder="Select fix to apply"
            onChange={handleActionSelect}
            isMulti={false}
          />
        </div>
      </div>
    </div>
  );
};

const InactiveMetadataCard: FC<{
  affectedDefinitions: Array<SampleDefinitionFormData>;
  issue: Extract<SampleDefinitionIssue, { type: 'inactive-metadata' }>;
  onAction: (action: BulkIssueAction) => void;
}> = ({ affectedDefinitions, issue, onAction }) => {
  const actionOptions: Array<IssueActionOption> = [
    { value: 'clear-invalid-metadata', label: 'Remove inactive metadata' },
    { value: 'remove-definition', label: 'Remove entire definition' },
  ];

  const definitionIds = affectedDefinitions.map((def) => def.api_id);

  const handleActionSelect = (action: SingleValue<IssueActionOption>) => {
    switch (action?.value) {
      case 'clear-invalid-metadata':
        onAction({
          type: 'bulk-clear-invalid-metadata',
          definition_ids: definitionIds,
          metadata_ids: issue.inactive_metadata_ids,
        });
        break;
      case 'remove-definition':
        onAction({ type: 'bulk-remove-definitions', definition_ids: definitionIds });
        break;
      default:
        errorToast('Something went wrong, please try again');
    }
  };

  return (
    <div className={styles.issueCard} data-testid="inactive-metadata-card">
      <div className="flex">
        <div className="pr2 pl1">
          <RiAlertFill className={`red ${styles.alertIcon}`} size={16} />
        </div>
        <div className={styles.issueContent}>
          <div className={styles.issueTitle}>Inactive metadata</div>
          <div className={styles.issueDescription}>
            {issue.inactive_metadata_ids.length} metadata field{issue.inactive_metadata_ids.length > 1 ? 's' : ''} (
            {_isNotEmpty(issue.metadata_names) ? issue.metadata_names.join(', ') : 'unknown'}){' '}
            {issue.inactive_metadata_ids.length > 1 ? 'have' : 'has'} been deactivated.
          </div>
          <AffectedDefinitionsList definitions={affectedDefinitions} />
          <Select
            options={actionOptions}
            placeholder="Select fix to apply"
            onChange={handleActionSelect}
            isMulti={false}
          />
        </div>
      </div>
    </div>
  );
};

const MissingMetadataCard: FC<{
  affectedDefinitions: Array<SampleDefinitionFormData>;
  issue: Extract<SampleDefinitionIssue, { type: 'missing-metadata' }>;
  onAction: (action: BulkIssueAction) => void;
}> = ({ affectedDefinitions, issue, onAction }) => {
  const actionOptions: Array<IssueActionOption> = [
    { value: 'clear-invalid-metadata', label: 'Remove missing metadata' },
    { value: 'remove-definition', label: 'Remove entire definition' },
  ];

  const definitionIds = affectedDefinitions.map((def) => def.api_id);

  const handleActionSelect = (action: SingleValue<IssueActionOption>) => {
    switch (action?.value) {
      case 'clear-invalid-metadata':
        onAction({
          type: 'bulk-clear-invalid-metadata',
          definition_ids: definitionIds,
          metadata_ids: issue.missing_metadata_ids,
        });
        break;
      case 'remove-definition':
        onAction({ type: 'bulk-remove-definitions', definition_ids: definitionIds });
        break;
      default:
        errorToast('Something went wrong, please try again');
    }
  };

  return (
    <div className={styles.issueCard} data-testid="missing-metadata-card">
      <div className="flex">
        <div className="pr2 pl1">
          <RiAlertFill className={`red ${styles.alertIcon}`} size={16} />
        </div>
        <div className={styles.issueContent}>
          <div className={styles.issueTitle}>Metadata not found</div>
          <div className={styles.issueDescription}>
            {issue.missing_metadata_ids.length} metadata field{issue.missing_metadata_ids.length > 1 ? 's' : ''} no
            longer {issue.missing_metadata_ids.length > 1 ? 'exist' : 'exists'} in the glossary.
          </div>
          <AffectedDefinitionsList definitions={affectedDefinitions} />
          <Select
            options={actionOptions}
            placeholder="Select fix to apply"
            onChange={handleActionSelect}
            isMulti={false}
          />
        </div>
      </div>
    </div>
  );
};

const InvalidMetadataValueCard: FC<{
  affectedDefinitions: Array<SampleDefinitionFormData>;
  issue: Extract<SampleDefinitionIssue, { type: 'invalid-metadata-value' }>;
  onAction: (action: BulkIssueAction) => void;
}> = ({ affectedDefinitions, issue, onAction }) => {
  // Check if we can surgically remove invalid options (requires valid JSON array with mixed valid/invalid values)
  const canRemoveSelectively = (() => {
    if (issue.field_type !== 'multi_select' || !_notNil(issue.invalid_values) || issue.invalid_values.length === 0) {
      return false;
    }

    try {
      const parsed = JSON.parse(issue.stored_value);
      if (!Array.isArray(parsed)) {
        return false;
      }
      // Only show "Remove invalid options" if there are valid values to preserve
      return parsed.length > issue.invalid_values.length;
    } catch {
      return false;
    }
  })();

  const actionOptions: Array<IssueActionOption> = canRemoveSelectively
    ? [
        { value: 'clear-invalid-options', label: 'Remove invalid options' },
        { value: 'clear-invalid-metadata', label: 'Clear entire field' },
        { value: 'remove-definition', label: 'Remove entire definition' },
      ]
    : [
        { value: 'clear-invalid-metadata', label: 'Clear invalid metadata' },
        { value: 'remove-definition', label: 'Remove entire definition' },
      ];

  const definitionIds = affectedDefinitions.map((def) => def.api_id);

  const handleActionSelect = (action: SingleValue<IssueActionOption>) => {
    switch (action?.value) {
      case 'clear-invalid-options':
        if (issue.field_type === 'multi_select' && _notNil(issue.invalid_values)) {
          onAction({
            type: 'bulk-clear-invalid-metadata-options',
            definition_ids: definitionIds,
            metadata_id: issue.metadata_id,
            invalid_values: issue.invalid_values,
          });
        }
        break;
      case 'clear-invalid-metadata':
        onAction({
          type: 'bulk-clear-invalid-metadata',
          definition_ids: definitionIds,
          metadata_ids: [issue.metadata_id],
        });
        break;
      case 'remove-definition':
        onAction({ type: 'bulk-remove-definitions', definition_ids: definitionIds });
        break;
      default:
        errorToast('Something went wrong, please try again');
    }
  };

  const decodeValue = (value: string): string => {
    try {
      return decodeURIComponent(value);
    } catch (decodeError) {
      ExceptionHandler.captureException(
        new InVivoError('Failed to decode URL-encoded metadata value', {
          slug: 'metadata-value-decode-failure',
          cause: decodeError as Error,
          context: {
            value,
            issue_type: issue.type,
          },
        })
      );
      return value;
    }
  };

  const getDisplayStoredValue = (): string => {
    try {
      const parsed = JSON.parse(issue.stored_value);
      if (Array.isArray(parsed)) {
        return parsed.map(decodeValue).join(', ');
      }
      return decodeValue(issue.stored_value);
    } catch (parseError) {
      ExceptionHandler.captureException(
        new InVivoError('Failed to parse stored metadata value for display', {
          slug: 'metadata-display-value-parse-failure',
          cause: parseError as Error,
          context: {
            metadata_id: issue.metadata_id,
            stored_value: issue.stored_value,
          },
        })
      );
      return decodeValue(issue.stored_value);
    }
  };

  const displayStoredValue = getDisplayStoredValue();
  const displayOptions = issue.available_options.map(decodeValue).join(', ');

  return (
    <div className={styles.issueCard} data-testid="invalid-metadata-value-card">
      <div className="flex">
        <div className="pr2 pl1">
          <RiAlertFill className={`red ${styles.alertIcon}`} size={16} />
        </div>
        <div className={styles.issueContent}>
          <div className={styles.issueTitle}>Invalid metadata value</div>
          <div className={styles.issueDescription}>
            The metadata field "{issue.metadata_name}" contains an invalid value "{displayStoredValue}". Valid options
            are: {displayOptions}.
          </div>
          <AffectedDefinitionsList definitions={affectedDefinitions} />
          <Select
            options={actionOptions}
            placeholder="Select fix to apply"
            onChange={handleActionSelect}
            isMulti={false}
          />
        </div>
      </div>
    </div>
  );
};

const MissingRequiredMetadataCard: FC<{
  affectedDefinitions: Array<SampleDefinitionFormData>;
  issue: Extract<SampleDefinitionIssue, { type: 'missing-required-metadata' }>;
  onAction: (action: BulkIssueAction) => void;
}> = ({ affectedDefinitions, issue, onAction }) => {
  const actionOptions: Array<IssueActionOption> = [
    { value: 'edit-definition', label: 'Edit and add required metadata' },
    { value: 'remove-definition', label: 'Remove entire definition' },
  ];

  const definitionIds = affectedDefinitions.map((def) => def.api_id);

  const handleActionSelect = (action: SingleValue<IssueActionOption>) => {
    switch (action?.value) {
      case 'edit-definition':
        onAction({ type: 'open-sample-plan-modal' });
        break;
      case 'remove-definition':
        onAction({ type: 'bulk-remove-definitions', definition_ids: definitionIds });
        break;
      default:
        errorToast('Something went wrong, please try again');
    }
  };

  const fieldNames = issue.missing_required_metadata.map((m) => m.metadata_name).join(', ');

  return (
    <div className={styles.issueCard} data-testid="missing-required-metadata-card">
      <div className="flex">
        <div className="pr2 pl1">
          <RiAlertFill className={`red ${styles.alertIcon}`} size={16} />
        </div>
        <div className={styles.issueContent}>
          <div className={styles.issueTitle}>Missing required metadata</div>
          <div className={styles.issueDescription}>
            {issue.missing_required_metadata.length} required metadata field
            {issue.missing_required_metadata.length > 1 ? 's are' : ' is'} missing or empty: {fieldNames}.
          </div>
          <AffectedDefinitionsList definitions={affectedDefinitions} />
          <Select
            options={actionOptions}
            placeholder="Select fix to apply"
            onChange={handleActionSelect}
            isMulti={false}
          />
        </div>
      </div>
    </div>
  );
};

const InvalidTargetCard: FC<{
  affectedDefinitions: Array<SampleDefinitionFormData>;
  issue: Extract<SampleDefinitionIssue, { type: 'invalid-target' }>;
  onAction: (action: BulkIssueAction) => void;
}> = ({ affectedDefinitions, issue, onAction }) => {
  const getTargetDescription = (): string => {
    switch (issue.target.type) {
      case 'groups':
        return `${issue.target.missing_group_ids.length} treatment group${
          issue.target.missing_group_ids.length > 1 ? 's' : ''
        } ${issue.target.missing_group_ids.length > 1 ? 'have' : 'has'} been removed from the study${
          _isNotEmpty(issue.target.group_names) ? ` (${issue.target.group_names.join(', ')})` : ''
        }.`;
      case 'registered_animals':
        return `${issue.target.missing_animal_ids.length} registered animal${
          issue.target.missing_animal_ids.length > 1 ? 's' : ''
        } ${issue.target.missing_animal_ids.length > 1 ? 'have' : 'has'} been removed from the study${
          _isNotEmpty(issue.target.animal_names) ? ` (${issue.target.animal_names.join(', ')})` : ''
        }.`;
      case 'planned_animals':
        return `${issue.target.missing_animal_ids.length} planned animal${
          issue.target.missing_animal_ids.length > 1 ? 's' : ''
        } ${issue.target.missing_animal_ids.length > 1 ? 'have' : 'has'} been removed from the study.`;
    }
  };

  const actionOptions: Array<IssueActionOption> = [
    { value: 'remove-invalid-targets', label: 'Remove invalid targets' },
    { value: 'remove-definition', label: 'Remove entire definition' },
  ];

  const definitionIds = affectedDefinitions.map((def) => def.api_id);

  const handleActionSelect = (action: SingleValue<IssueActionOption>) => {
    switch (action?.value) {
      case 'remove-invalid-targets':
        // Handle each target type explicitly for proper type inference
        switch (issue.target.type) {
          case 'groups':
            onAction({
              type: 'bulk-remove-invalid-targets',
              definition_ids: definitionIds,
              target_type: 'groups',
              target_ids: issue.target.missing_group_ids,
            });
            break;
          case 'registered_animals':
            onAction({
              type: 'bulk-remove-invalid-targets',
              definition_ids: definitionIds,
              target_type: 'registered_animals',
              target_ids: issue.target.missing_animal_ids,
            });
            break;
          case 'planned_animals':
            onAction({
              type: 'bulk-remove-invalid-targets',
              definition_ids: definitionIds,
              target_type: 'planned_animals',
              target_ids: issue.target.missing_animal_ids,
            });
            break;
        }
        break;
      case 'remove-definition':
        onAction({ type: 'bulk-remove-definitions', definition_ids: definitionIds });
        break;
      default:
        errorToast('Something went wrong, please try again');
    }
  };

  return (
    <div className={styles.issueCard} data-testid="invalid-target-card">
      <div className="flex">
        <div className="pr2 pl1">
          <RiAlertFill className={`red ${styles.alertIcon}`} size={16} />
        </div>
        <div className={styles.issueContent}>
          <div className={styles.issueTitle}>Invalid targets</div>
          <div className={styles.issueDescription}>{getTargetDescription()}</div>
          <AffectedDefinitionsList definitions={affectedDefinitions} />
          <Select
            options={actionOptions}
            placeholder="Select fix to apply"
            onChange={handleActionSelect}
            isMulti={false}
          />
        </div>
      </div>
    </div>
  );
};
